var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { staticFirstWeekDay } from '@folio/stripes/components';
import memoizee from 'memoizee';
import { useMemo } from 'react';
import { getLocalizedTime } from './DateUtils';
/**
 * Used for algorithmically relating weekdays to each other.  This MUST not be
 * used for any type of user display, only for relating weekdays to each other.
 * The only guarantees are that weekdays (when wrapped around) will be in the
 * typical order, such as Friday -> Saturday, and that .getDay() on Date will
 * correspond here as expected.
 * Additionally, the values here will correspond to {@link WEEKDAY_INDEX}
 */
export var WEEKDAYS = {
    SUNDAY: 0,
    MONDAY: 1,
    TUESDAY: 2,
    WEDNESDAY: 3,
    THURSDAY: 4,
    FRIDAY: 5,
    SATURDAY: 6
};
/**
 * Used for algorithmically relating weekdays to each other.  This MUST not be
 * used for any type of user display, only for relating weekdays to each other.
 * The only guarantees are that weekdays (when wrapped around) will be in the
 * typical order, such as Friday -> Saturday, and that .getDay() on Date will
 * correspond here as expected.
 * Additionally, the values here will correspond to {@link WEEKDAY_INDEX}
 */
export var WEEKDAY_INDEX = [
    'SUNDAY',
    'MONDAY',
    'TUESDAY',
    'WEDNESDAY',
    'THURSDAY',
    'FRIDAY',
    'SATURDAY'
];
export var getFirstDayOfWeek = memoizee(function (locale) {
    var _a, _b;
    var region = (_b = (_a = locale.split('-')[1]) === null || _a === void 0 ? void 0 : _a.toUpperCase()) !== null && _b !== void 0 ? _b : 'US';
    var weekdayLookup = {
        sun: 0,
        mon: 1,
        tue: 2,
        wed: 3,
        thu: 4,
        fri: 5,
        sat: 6
    };
    for (var _i = 0, _c = Object.entries(staticFirstWeekDay); _i < _c.length; _i++) {
        var _d = _c[_i], weekday = _d[0], regions = _d[1];
        if (regions.includes(region)) {
            return weekdayLookup[weekday];
        }
    }
    return 0; // safe default
});
/** Get information for the days of the week, for the current locale */
export var getLocaleWeekdays = memoizee(function (intl) {
    var firstDay = getFirstDayOfWeek(intl.locale);
    var weekdays = [];
    for (var i = 0; i < 7; i++) {
        // need to be careful to use UTC here, and force react-intl to use UTC
        // since this is the one date-formatted thing that will be visible to users
        var day = new Date(Date.UTC(2000, 1, 1));
        day.setUTCDate(day.getUTCDate() - day.getUTCDay() + firstDay + i);
        weekdays.push({
            weekday: WEEKDAY_INDEX[(firstDay + i) % 7],
            short: intl.formatDate(day, { weekday: 'short', timeZone: 'UTC' }),
            long: intl.formatDate(day, { weekday: 'long', timeZone: 'UTC' })
        });
    }
    return weekdays;
});
export function useLocaleWeekdays(intl) {
    return useMemo(function () { return getLocaleWeekdays(intl); }, [intl]);
}
/** Get a range of weekdays between two, inclusive */
export function getWeekdayRange(start, end) {
    var startIndex = WEEKDAYS[start];
    var endIndex = WEEKDAYS[end];
    var days = [WEEKDAY_INDEX[startIndex]];
    while (startIndex !== endIndex) {
        startIndex = (startIndex + 1) % 7;
        days.push(WEEKDAY_INDEX[startIndex]);
    }
    return days;
}
/**
 * Get all weekdays covered by a calendar's opening.  Openings that wrap around onto the
 * same day (e.g. M 12:00 - M 11:59) will include that weekday on both sides
 * (e.g. M T W R F S N M)
 */
export function getWeekdaySpan(opening) {
    if (opening.startDay === opening.endDay) {
        // wraps around (starts after it sends, e.g. M 12:00 -> M 11:59)
        if (opening.startTime > opening.endTime) {
            return __spreadArray(__spreadArray([
                opening.startDay
            ], WEEKDAY_INDEX.slice(WEEKDAYS[opening.startDay] + 1), true), WEEKDAY_INDEX.slice(0, WEEKDAYS[opening.startDay] + 1), true);
        }
        return [opening.startDay];
    }
    return getWeekdayRange(opening.startDay, opening.endDay);
}
/**
 * Determine how close a weekday is relative to a given reference date.
 * For weekdays more than a day away, the weekday will be returned in the payload
 * for additional formatting.  It is not included for sameDay/nextDay
 */
export function getRelativeWeekdayStatus(intl, weekday, time, referenceDate) {
    if (referenceDate.getDay() === WEEKDAYS[weekday]) {
        return {
            proximity: 'sameDay',
            weekday: undefined,
            date: undefined,
            time: getLocalizedTime(intl, time)
        };
    }
    if ((referenceDate.getDay() + 1) % 7 === WEEKDAYS[weekday]) {
        return {
            proximity: 'nextDay',
            weekday: undefined,
            date: undefined,
            time: getLocalizedTime(intl, time)
        };
    }
    return {
        proximity: 'otherWeekday',
        weekday: weekday,
        date: undefined,
        time: getLocalizedTime(intl, time)
    };
}
/** Determine if a day is between two weekdays, inclusive */
export function weekdayIsBetween(testWeekday, start, end) {
    var startIndex = WEEKDAYS[start];
    var endIndex = WEEKDAYS[end];
    // handles wraparounds, eg FRI (5) -> MON (1) converts to FRI (5) -> MON (8)
    // ensures startIndex <= endIndex
    if (startIndex > endIndex) {
        endIndex += 7;
    }
    // potentially shifts the bounds by a week to handle examples like above, if SUN (0) is queried
    if (startIndex > testWeekday.getDay()) {
        startIndex -= 7;
        endIndex -= 7;
    }
    return startIndex <= testWeekday.getDay() && testWeekday.getDay() <= endIndex;
}
